import './step.less'

import {RegistrationModel} from 'models/registration/RegistrationModel'
import {StepModel} from 'models/registration/StepModel'
import React, {useEffect} from 'react'
import {LinkProps} from 'util/link'
import {scrollTo} from 'util/scrollmemory'
import {style} from 'util/style'
import {BreadCrumbs, BreadCrumbsItem} from 'views/registration/breadcrumbs'
import {ButtonModType, Theme} from 'views/theme'

const styles = style('step', ['intro', 'buttons', 'prev', 'next'])
declare var dataLayer: Array<any>

export const Step: React.FC<{
	hasFormSubmit?: boolean
	isSubmitting?: boolean
	isWaiting?: boolean
	hideNextButton?: boolean
	step: StepModel
}> = ({
	step,
	isSubmitting,
	isWaiting,
	hideNextButton,
	hasFormSubmit = false,
	children
}) => {
	const registration = step.registration

	useEffect(() => {
		if (!window.dataLayer) return
		dataLayer.push({
			event: 'VirtualPageview',
			virtualPageURL: step.registration.config.url + '#' + step.key,
			virtualPageTitle: step.registration.event.title,
			anonymize_ip: true
		})
	}, [])

	useEffect(() => {
		window.dataLayer = window.dataLayer || []
		if (!window.dataLayer) return
		if (!registration) return
		if (!['breakouts', 'configure', 'details', 'summary'].includes(step.key))
			return

		const listData = {
			item_list_id: registration.config.url.substring(1).replace(/\//g, '_'),
			item_list_name: registration.event.title
		}

		let dataLayer = null
		if (step.key === 'breakouts') {
			dataLayer = {
				event: 'select_item',
				ecommerce: {
					...listData,
					items: registration.packs.map(pack => ({
						item_category: 'event',
						item_id: pack.config.key,
						item_name: pack.config.label,
						price: pack.config.price
					}))
				}
			}
		}
		if (step.key === 'configure' || step.key === 'details') {
			dataLayer = {
				event: 'select_item',
				ecommerce: {
					...listData,
					items: registration.options
						.map(option => {
							if (!option) return null
							if (
								(step.key === 'configure' &&
									(option.config.key === 'dinner' ||
										option.config.key === 'reception')) ||
								(step.key === 'details' && option.config.key !== 'dinner')
							)
								return null
							const selected = option.getChoiceFor(registration)
							if (selected) {
								return {
									item_category: option.key,
									item_id: selected.key,
									item_name: selected.label,
									price: selected.config.price
								}
							}
						})
						.filter(i => Boolean(i))
				}
			}
		}
		if (step.key === 'summary') {
			dataLayer = {
				event: 'begin_checkout',
				ecommerce: {
					...listData,
					items: [
						...registration.packs.map(pack => ({
							item_category: 'event',
							item_id: pack.config.key,
							item_name: pack.config.label,
							price: pack.config.price
						})),
						...registration.options
							.map(option => {
								if (!option) return null
								const selected = option.getChoiceFor(registration)
								if (selected) {
									return {
										item_category: option.key,
										item_id: selected.key,
										item_name: selected.label,
										price: selected.config.price
									}
								}
							})
							.filter(i => Boolean(i))
					]
				}
			}
		}
		window.dataLayer.push(dataLayer)
	}, [step.key])

	// Scroll to buttons when pack is selected
	const handleClick = (event: MouseEvent) => {
		if (step.key !== 'select') return
		if (
			Array.from((event.target as HTMLElement).classList).some(item =>
				item.startsWith('pack')
			)
		)
			scrollTo(() => 0, document.querySelector('.step_buttons'))
	}

	useEffect(() => {
		addEventListener('click', handleClick)
		return () => removeEventListener('click', handleClick)
	}, [])

	const nextButtonAttrs: LinkProps & ButtonModType = {
		mod: [
			'gradient',
			isSubmitting ? 'submitting' : '',
			!step.isReady ? 'inactive' : ''
		]
	}

	if (!isSubmitting && !isWaiting) {
		if (hasFormSubmit) {
			nextButtonAttrs.as = 'button'
			nextButtonAttrs.type = 'submit'
			nextButtonAttrs.disabled = isWaiting
		}
		{
			nextButtonAttrs.to = `#${step.nextStepKey}`
		}
	}

	if (step.key === 'summary') {
		nextButtonAttrs.as = 'button'
		nextButtonAttrs.type = 'submit'
	}

	const backLabel = step?.back_btn || registration.general.back_btn
	let nextLabel = step?.next_btn || registration.general.next_btn

	if (step.key === 'onsite') nextLabel = 'Confirm'
	if (isSubmitting) nextLabel = 'Submitting..'

	return (
		<div className={styles}>
			<StepIntro active={step} registration={registration} />
			<div className={styles('content')}>{children}</div>
			<StepButtons>
				{step.key !== 'badge' && !step.isFirst && (
					<StepPrev to={`#${step.previousStepKey}`}>{backLabel}</StepPrev>
				)}
				{!hideNextButton && step.key !== 'badge' && (
					<StepNext
						{...nextButtonAttrs}
						disabled={step.key === 'summary' && isWaiting}
					>
						{nextLabel}
					</StepNext>
				)}
			</StepButtons>
			{step.key === 'summary' && isWaiting && (
				<p className={styles('submitlabel')}>
					Please accept the General terms & conditions and the Privacy
					statement.
				</p>
			)}
		</div>
	)
}

export const StepIntro: React.FC<{
	active: StepModel
	registration: RegistrationModel
}> = ({active, registration}) => {
	const steps = registration.steps
	const current = steps.indexOf(active)
	return (
		<div {...styles.intro()}>
			<BreadCrumbs>
				{registration.steps.map((step, i) => (
					<BreadCrumbsItem
						to={active.key === 'badge' ? undefined : i < current && step.key}
						active={step.key === active.key}
					>
						{step.breadcrumb}
					</BreadCrumbsItem>
				))}
			</BreadCrumbs>
			<h2 {...styles.intro('title')}>{active.title}</h2>
			<Theme.Wysiwyg {...styles.intro('text')}>{active.text}</Theme.Wysiwyg>
		</div>
	)
}

export const StepButtons: React.FC<{children: Array<any>}> = ({children}) => {
	return (
		<div className={styles.buttons}>
			{children.map(child => (
				<div>{child}</div>
			))}
		</div>
	)
}

export const StepPrev: React.FC<{to?: string}> = ({children, ...rest}) => {
	return (
		<div className={styles.prev}>
			<Theme.Button mod={['gradient']} {...rest}>
				<Theme.ButtonIcon icon="arrow-left" />
				<Theme.ButtonText>{children}</Theme.ButtonText>
			</Theme.Button>
		</div>
	)
}

export const StepNext: React.FC<{
	inactive?: boolean
	submitting?: boolean
	disabled?: boolean
}> = ({inactive, submitting, disabled, children, ...rest}) => {
	return (
		<div className={styles.next}>
			<Theme.Button
				mod={['gradient', {inactive}, {submitting}]}
				{...rest}
				disabled={disabled}
			>
				<Theme.ButtonText>{children}</Theme.ButtonText>
				<Theme.ButtonIcon icon="arrow-right" />
			</Theme.Button>
		</div>
	)
}
